<template>
  <scroll-zoom-wrapper
    v-if="zoomFactor"
    ref="viewer"
    v-model="zoomFactor"
    buttons-position="top"
    class="document-viewer d-flex flex-column flex-grow-1"
    :class="{ dark: onDark }"
    :min-zoom="0.25"
    :max-zoom="5"
    :show-controls="showZoomControls"
    @rotate="$emit('rotate')"
  >
    <document-page
      v-for="(__, pageIndex) in displayedPages"
      :id="`document-page-${pageIndex + 1}`"
      ref="pages"
      :key="`${pageIndex}-${document.id}`"
      class="document-page"
      :calculations-enabled="calculationsEnabled"
      :document="document"
      :rotation="rotation"
      :page-index="pageIndex"
      :page-overlays="overlays[pageIndex]"
      :zoom-factor="zoomFactor"
      :show-page-check-signals="showPageCheckSignals"
      :on-dark="onDark"
    />

    <template #buttons>
      <document-view-tool-button
        id="document-rotate"
        tooltip-title="Rotate"
        data-testid="document-rotate"
        icon="medium-arrows-rotate"
        class="i-mb-2"
        @button-click="$emit('rotate')"
      />
      <document-view-tool-button
        v-if="showDocumentCalculatorButton"
        id="document-calculator-toggle"
        tooltip-title="Calculator"
        :class="{ enabled: calculationsEnabled }"
        data-testid="document-calculator-toggle"
        icon="medium-money-calculator-1"
        @button-click="toggleDocumentCalculator"
      />
    </template>
    <div v-if="pagesToDisplay < document.total_pages">
      <i-spinner class="i-mt-5"></i-spinner>
    </div>
  </scroll-zoom-wrapper>
</template>

<script>
import * as fileTypes from '../../constants/fileTypes';
import ScrollZoomWrapper from './ScrollZoomWrapper';
import DocumentPage from './DocumentPage';
import { mapGetters } from 'vuex';
import DocumentViewToolButton from '@/V2/documents/document-view/DocumentViewToolButton';
import {
  closeAllTooltips,
  iButton,
  iButtonStyles,
  iButtonSizes,
  iButtonTypes,
  iSpinner,
  iTooltip,
  iTooltipAlignmentMode,
} from '@inscribe/signal';

const TYPES_USE_PDF_TEXT = [fileTypes.TRUE_PDF, fileTypes.SCAN_OCR];

const DEFAULT_PAGES_TO_SHOW = 10;

export default {
  components: {
    DocumentViewToolButton,
    iButton,
    iSpinner,
    iTooltip,
    'document-page': DocumentPage,
    'scroll-zoom-wrapper': ScrollZoomWrapper,
  },
  props: {
    document: {
      type: Object,
      required: true,
    },
    rotation: {
      type: Number,
      required: true,
    },
    overlays: {
      type: Array,
      required: true,
    },
    showPageCheckSignals: {
      type: Boolean,
      default: true,
    },
    showZoomControls: {
      type: Boolean,
      default: true,
    },
    onDark: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      calculationsEnabled: false,
      iButtonTypes,
      iButtonStyles,
      iButtonSizes,
      iTooltipAlignmentMode,
      pagesToDisplay:
        parseInt(this.$route.query.documentPage || 1) +
        (DEFAULT_PAGES_TO_SHOW - 1),
      zoomFactor: null,
      scrollTimeout: null,
    };
  },
  computed: {
    ...mapGetters({
      getDocumentById: 'getDocumentById',
      useOcrForTruPdfCalculations: 'useOcrForTruPdfCalculations',
    }),
    displayedPages() {
      return [...Array(this.document.total_pages)].slice(
        0,
        this.pagesToDisplay,
      );
    },

    showDocumentCalculatorButton() {
      const doc = this.getDocumentById(this.document.id);
      if (!doc) {
        return false;
      }
      return (
        this.rotation === 0 &&
        ((TYPES_USE_PDF_TEXT.includes(this.document.file_type) &&
          !this.useOcrForTruPdfCalculations) ||
          doc.ocrPages)
      );
    },
  },
  watch: {
    rotation() {
      this.calculationsEnabled = false;
    },
    '$route.query.documentPage': {
      immediate: true,
      handler(documentPage) {
        if (documentPage) {
          this.updatePagesToDisplay(documentPage);
          this.moveToPage(documentPage);
        }
      },
    },
    '$route.query.instance': {
      immediate: true,
      handler(instanceId) {
        if (instanceId) {
          const instanceElement = document.querySelector(`#${instanceId}`);
          setTimeout(() => {
            instanceElement && instanceElement.scrollIntoView();
          });
        }
      },
    },
  },
  mounted() {
    document.addEventListener('keydown', this.maybeHandleSelectAll);
    if (
      !TYPES_USE_PDF_TEXT.includes(this.document.file_type) ||
      this.useOcrForTruPdfCalculations
    ) {
      this.fetchDocumentOcr();
    }
    this.$el?.addEventListener('scroll', closeAllTooltips);

    setTimeout(() => {
      this.zoomFactor = this.getInitialZoomFactor();
      this.$nextTick(() => {
        this.$refs?.viewer?.$el?.addEventListener(
          'scroll',
          this.checkShouldLoadMorePages,
        );
      });
    });
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.maybeHandleSelectAll);
    this.$el?.removeEventListener('scroll', closeAllTooltips);
    this.$refs?.viewer?.$el?.removeEventListener(
      'scroll',
      this.checkShouldLoadMorePages,
    );
  },
  methods: {
    fetchDocumentOcr() {
      this.$store
        .dispatch('getDocumentOcr', {
          customerId: this.document.customer_id,
          documentId: this.document.id,
        })
        .then(({ data }) => {
          this.$store.commit('updateGeneric', {
            objectsName: 'documents',
            updatedObject: {
              ...this.document,
              ocrPages: data.data,
            },
          });
        });
    },
    maybeHandleSelectAll(event) {
      if (
        (event.code === 'KeyA' || event.keyCode === 65) &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault();
        const selection = document.getSelection();
        const textNodes = document.querySelectorAll('.textLayer span');
        selection.setBaseAndExtent(
          textNodes[0],
          0,
          textNodes[textNodes.length - 1],
          0,
        );
      }
    },
    moveToPage(pageNum) {
      const page = this.$refs.pages && this.$refs.pages[pageNum - 1];
      if (page && page.$refs.documentPage) {
        page.$refs.documentPage.scrollIntoView();
      } else if (pageNum - 1 <= this.document.total_pages) {
        setTimeout(() => {
          this.moveToPage(pageNum);
        }, 500);
      }
    },
    pageElementIsVisible(elm) {
      var rect = elm.getBoundingClientRect();
      var viewHeight = Math.max(
        document.documentElement.clientHeight,
        window.innerHeight,
      );
      return !(rect.bottom < 0 || rect.top - viewHeight >= 0);
    },
    checkShouldLoadMorePages() {
      this.scrollTimeout && clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => {
        const pages = document.querySelectorAll(
          '.document-viewer > .document-page',
        );
        const lastPage = pages[pages.length - 1];
        if (lastPage && this.pageElementIsVisible(lastPage)) {
          this.loadMorePages(pages.length);
        }
      }, 50);
    },
    loadMorePages(pagesLength) {
      const numberOfMorePagesToDisplay = 2;
      this.updatePagesToDisplay(pagesLength + numberOfMorePagesToDisplay);
    },
    updatePagesToDisplay(pageNum) {
      this.pagesToDisplay = Math.max(this.pagesToDisplay, parseInt(pageNum));
    },
    toggleDocumentCalculator() {
      this.calculationsEnabled = !this.calculationsEnabled;
    },
    getInitialZoomFactor() {
      let defaultZoom = 0.5;
      if (this.document.file_type !== fileTypes.IMAGE) {
        if (window.innerWidth < 1584) {
          defaultZoom = 0.75;
        } else if (window.innerWidth < 1800) {
          defaultZoom = 1;
        } else {
          defaultZoom = 1.25;
        }
      } else {
        defaultZoom = 0.75;
      }
      return defaultZoom;
    },
  },
};
</script>

<style lang="scss">
.document-viewer {
  max-height: 100%;
  overflow: auto;
  background-image: url('@/img/dot-background-light.png');
  background-attachment: local;
  background-repeat: repeat;
  background-size: 16px;
  background-position: 8px 8px;

  &.dark {
    background-image: url('@/img/dot-background-dark.png');
    background-attachment: local; // needs to be set both times otherwise it doesn't scroll correctly
  }

  .document-page {
    overflow: visible;
    position: relative;
    transform-origin: center center;

    .rotation-wrapper-outer {
      display: table;
    }

    .rotation-wrapper-inner {
      padding: 50% 0;
      height: 0;
    }

    .document-img {
      display: block;
      position: relative;
      height: auto;
      transform-origin: top left;
      margin-top: -50%;

      &.rotate-90 {
        transform: rotate(90deg) translateY(-100%);
      }

      &.rotate-180 {
        transform: rotate(180deg) translateX(-100%) translateY(-100%);
      }

      &.rotate-270 {
        transform: rotate(270deg) translate(-100%);
      }
    }
  }

  .page-divider {
    color: $color-navy-blue-60;
  }

  .document-view-tool-button {
    &.enabled {
      background: $light-neutral-30;
    }
  }
}
</style>
